import React, { useState } from 'react';
import { ChevronDownIcon, CheckIcon, SparklesIcon } from '@heroicons/react/24/outline';
import { supabase } from '../services/supabase';
import toast from 'react-hot-toast';
import { TARGETING_CATEGORIES } from '../constants/targetingCategories';

interface ContentCategoryModalProps {
  isOpen: boolean;
  talentId: string;
  onSaved: (category: string) => void;
}

// Forced (non-dismissable) modal that asks a sponsor-enabled creator to pick a
// primary content category so we can target the right pre-paid sponsors. There
// is intentionally no close button / backdrop dismiss — they must choose one.
const ContentCategoryModal: React.FC<ContentCategoryModalProps> = ({ isOpen, talentId, onSaved }) => {
  const [selected, setSelected] = useState<string>('');
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  if (!isOpen) return null;

  const handleSave = async () => {
    if (!selected || saving) return;
    setSaving(true);
    try {
      const { error } = await supabase
        .from('talent_profiles')
        .update({ targeting_category: selected })
        .eq('id', talentId);
      if (error) throw error;
      toast.success('Thanks! We\u2019ll match you with the right sponsors.');
      onSaved(selected);
    } catch (err) {
      console.error('Failed to save targeting category:', err);
      toast.error('Could not save. Please try again.');
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
      <div className="bg-[#111] rounded-2xl border border-white/10 w-full max-w-md overflow-visible">
        <div className="p-6 space-y-5">
          <div className="flex items-center gap-2 text-green-400">
            <SparklesIcon className="w-5 h-5" />
            <span className="text-xs font-semibold uppercase tracking-wide">Pre-paid Sponsors</span>
          </div>

          <div>
            <h2 className="text-xl font-bold text-white leading-snug">
              What type of content do you primarily post?
            </h2>
            <p className="text-sm text-gray-400 mt-2 leading-relaxed">
              Our pre-paid sponsorship network is exploding, to better target sponsors for your
              audience, please choose one.
            </p>
          </div>

          {/* Dropdown */}
          <div className="relative">
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              className="w-full px-4 py-3 border border-white/15 rounded-xl bg-white/5 text-left flex items-center justify-between hover:border-white/30 transition-colors"
            >
              <span className={selected ? 'text-white' : 'text-gray-500'}>
                {selected || 'Select a content category...'}
              </span>
              <ChevronDownIcon className={`w-5 h-5 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
            </button>
            {open && (
              <div className="absolute z-10 w-full mt-1 rounded-xl shadow-2xl max-h-64 overflow-y-auto border border-white/15" style={{ backgroundColor: 'rgba(15, 23, 42, 0.98)', backdropFilter: 'blur(24px)' }}>
                {TARGETING_CATEGORIES.map((cat) => {
                  const isSelected = selected === cat;
                  return (
                    <button
                      key={cat}
                      type="button"
                      onClick={() => { setSelected(cat); setOpen(false); }}
                      className={`w-full px-4 py-2.5 text-left flex items-center justify-between gap-3 transition-colors hover:bg-white/10 ${isSelected ? 'bg-green-500/15' : ''}`}
                    >
                      <span className="text-gray-200 text-sm">{cat}</span>
                      {isSelected && <CheckIcon className="w-4 h-4 text-green-400 flex-shrink-0" />}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <button
            onClick={handleSave}
            disabled={!selected || saving}
            className="w-full py-3.5 bg-green-500 text-white font-semibold rounded-xl hover:bg-green-400 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {saving ? 'Saving...' : 'Save & Continue'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ContentCategoryModal;
